import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Star, ShieldCheck, Download, Clock } from 'lucide-react';
import { useCurrency } from '../contexts/CurrencyContext';
interface PricingPopupProps {
  isOpen: boolean;
  onClose: () => void;
}
const REVIEWS = [{
  name: 'Linda M.',
  location: 'Tennessee',
  text: "My granddaughter picked the 'Baking Day' ticket first. We made cookies and she talked the whole time. That never happens with a toy.",
  rating: 5
}, {
  name: 'Carol S.',
  location: 'Ontario',
  text: 'I was the grandma who always overthought gifts. This took me 10 minutes to set up and my grandson said it was his favorite present. I cried.',
  rating: 5
}, {
  name: 'Diane R.',
  location: 'Florida',
  text: "The redemption tracker is genius. We've done 4 tickets so far and my daughter says the kids ask about 'Grandma days' every week now.",
  rating: 5
}, {
  name: 'Patricia K.',
  location: 'Oregon',
  text: 'I have 5 grandkids ages 3–11. Printed one book for each. The little ones love picking tickets and the older ones actually schedule them.',
  rating: 5
}, {
  name: 'Margaret W.',
  location: 'Alberta',
  text: "Finally a gift that doesn't end up in the donation pile. My 7-year-old keeps her tickets in a special box. She calls them her 'Grandma coupons.'",
  rating: 5
}];
export function PricingPopup({
  isOpen,
  onClose
}: PricingPopupProps) {
  const {
    formatPrice
  } = useCurrency();
  const [currentReview, setCurrentReview] = useState(0);
  const nextReview = useCallback(() => {
    setCurrentReview(prev => (prev + 1) % REVIEWS.length);
  }, []);
  // Auto-rotate reviews
  useEffect(() => {
    if (!isOpen) return;
    const interval = setInterval(nextReview, 4500);
    return () => clearInterval(interval);
  }, [isOpen, nextReview]);
  // Lock body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  // Reset review index when opened
  useEffect(() => {
    if (isOpen) setCurrentReview(0);
  }, [isOpen]);
  const review = REVIEWS[currentReview];
  return <AnimatePresence data-id="element-157">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal/60 backdrop-blur-sm" onClick={onClose} data-id="element-158">
          <motion.div initial={{
        opacity: 0,
        y: 30,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} transition={{
        duration: 0.35,
        ease: [0.22, 1, 0.36, 1]
      }} onClick={e => e.stopPropagation()} className="bg-cream rounded-3xl shadow-2xl max-w-md w-full overflow-hidden border border-warm-gray/10 relative" data-id="element-159">
            {/* Close button */}
            <button onClick={onClose} className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white/80 text-warm-gray hover:bg-white hover:text-charcoal transition-colors shadow-sm" aria-label="Close" data-id="element-160">
              <X className="w-4 h-4" data-id="element-161" />
            </button>

            {/* Header */}
            <div className="bg-charcoal px-6 py-5 text-center" data-id="element-162">
              <p className="text-teal text-xs font-bold uppercase tracking-widest mb-1" data-id="element-163">
                Grandma & Me
              </p>
              <h3 className="text-cream font-serif text-2xl font-bold" data-id="element-164">
                Memory Tickets
              </h3>
            </div>

            {/* Pricing */}
            <div className="px-6 pt-6 pb-4 text-center" data-id="element-165">
              <div className="inline-block bg-coral/10 text-coral font-bold text-sm px-3 py-1 rounded-full mb-3" data-id="element-166">
                🎉 60% OFF — Limited Time
              </div>
              <div className="flex items-center justify-center gap-3 mb-1" data-id="element-167">
                <span className="text-xl text-warm-gray line-through" data-id="element-168">
                  {formatPrice(15.99)}
                </span>
                <span className="text-5xl font-bold text-coral" data-id="element-169">
                  {formatPrice(6.39)}
                </span>
              </div>
              <p className="text-warm-gray text-sm mb-5" data-id="element-170">
                One-time payment · Instant download · Yours forever
              </p>

              <a href="https://www.gemmamama.com/cart/45506271477899:1" target="_blank" rel="noopener noreferrer" className="w-full bg-coral hover:bg-coral-hover text-white text-lg font-bold py-4 rounded-xl shadow-lg shadow-coral/30 transition-all transform hover:-translate-y-0.5 flex items-center justify-center gap-2" data-id="element-171">
                <Download className="w-5 h-5" data-id="element-172" />
                Download Now — {formatPrice(6.39)}
              </a>

              <div className="flex items-center justify-center gap-2 mt-3 text-warm-gray/70 text-xs" data-id="element-173">
                <Clock className="w-3.5 h-3.5" data-id="element-174" />
                <span data-id="element-175">Ready in 7 minutes · Print at home</span>
              </div>
            </div>

            {/* Rotating Review */}
            <div className="px-6 pb-4" data-id="element-176">
              <div className="bg-white rounded-xl p-4 border border-stone-100 min-h-[120px] relative overflow-hidden" data-id="element-177">
                <AnimatePresence mode="wait" data-id="element-178">
                  <motion.div key={currentReview} initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -20
              }} transition={{
                duration: 0.35
              }} data-id="element-179">
                    {/* Stars */}
                    <div className="flex gap-0.5 mb-2" data-id="element-180">
                      {Array.from({
                    length: review.rating
                  }).map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" data-id="element-181" />)}
                    </div>
                    <p className="text-charcoal text-sm leading-relaxed mb-2 italic" data-id="element-182">
                      "{review.text}"
                    </p>
                    <p className="text-warm-gray text-xs font-medium" data-id="element-183">
                      — {review.name}, {review.location}
                    </p>
                  </motion.div>
                </AnimatePresence>

                {/* Review dots */}
                <div className="flex justify-center gap-1.5 mt-3" data-id="element-184">
                  {REVIEWS.map((_, i) => <button key={i} onClick={() => setCurrentReview(i)} className={`w-1.5 h-1.5 rounded-full transition-all ${i === currentReview ? 'bg-coral w-4' : 'bg-warm-gray/25 hover:bg-warm-gray/40'}`} aria-label={`Review ${i + 1}`} data-id="element-185" />)}
                </div>
              </div>
            </div>

            {/* Guarantee */}
            <div className="px-6 pb-6" data-id="element-186">
              <div className="flex items-start gap-2.5 bg-teal/5 p-3 rounded-lg border border-teal/10" data-id="element-187">
                <ShieldCheck className="w-5 h-5 text-teal shrink-0 mt-0.5" data-id="element-188" />
                <p className="text-xs text-warm-gray leading-relaxed" data-id="element-189">
                  <span className="font-bold text-charcoal" data-id="element-190">
                    100% Guarantee:
                  </span>{' '}
                  If it doesn't feel like something you'd be proud to gift, full
                  refund — no questions asked.
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}